import type { AgenticAiMessage } from './agentic-ai.models';
import type { TaskGraphTaskDto } from '../../views/ai-agent/ai-agent.types';

export const INTERVIEW_SCHEDULE_CHOICE_QUESTION =
  'Select up to 5 submission(s) to schedule the interview.';

/** Max submissions shown in the interview-schedule / Voice AI choice panel. */
export const INTERVIEW_SCHEDULE_MAX_CHOICE_ROWS = 100;

/** Max submissions the user may select and schedule in one request. */
export const INTERVIEW_SCHEDULE_MAX_SELECTIONS = 5;

export const VOICE_AI_CHOICE_QUESTION =
  'Select up to 5 submission(s) for the Voice AI interview.';

export const VOICE_AI_MAX_CHOICE_ROWS = INTERVIEW_SCHEDULE_MAX_CHOICE_ROWS;

export const VOICE_AI_MAX_SELECTIONS = INTERVIEW_SCHEDULE_MAX_SELECTIONS;

/** Shown when the user selects multi-job × multi-candidate pairs. */
export const MULTI_JOB_MULTI_APPLICANT_CHOICE_MESSAGE =
  'Please select either one candidate with multiple jobs, or one job with multiple candidates — not multiple candidates across multiple jobs.';

/** True when selected pairs span more than one job AND more than one candidate. */
export function isMultiJobMultiApplicantSelection(
  selections: { job_posting_id: string; job_seeker_id: string }[],
): boolean {
  const jobs = new Set<string>();
  const seekers = new Set<string>();
  for (const s of selections) {
    const j = (s.job_posting_id ?? '').trim();
    const a = (s.job_seeker_id ?? '').trim();
    if (j) {
      jobs.add(j);
    }
    if (a) {
      seekers.add(a);
    }
  }
  return jobs.size > 1 && seekers.size > 1;
}

/**
 * Task-graph wait / reply copy for interview choice.
 * When fewer than the max selection cap are available, state the real count.
 */
export function formatInterviewScheduleChoiceQuestion(availableCount: number): string {
  const n = Math.max(0, Math.floor(availableCount));
  const maxSel = INTERVIEW_SCHEDULE_MAX_SELECTIONS;
  if (n <= 0) {
    return 'No submissions are available to schedule an interview.';
  }
  if (n === 1) {
    return '1 submission available. Select it to schedule an interview, then continue.';
  }
  if (n < maxSel) {
    return `${n} submissions available. Select up to ${n} submission(s) to schedule an interview for, then continue.`;
  }
  return `Multiple submissions match. Select up to ${maxSel} submission(s) to schedule an interview for, then continue.`;
}

/** Right-panel subtitle for interview choice. */
export function formatInterviewScheduleChoicePanelSubtitle(availableCount: number): string {
  const n = Math.max(0, Math.floor(availableCount));
  const maxSel = INTERVIEW_SCHEDULE_MAX_SELECTIONS;
  if (n <= 0) {
    return 'No submissions available.';
  }
  if (n === 1) {
    return '1 submission available. Select it and choose who will conduct the interview.';
  }
  if (n < maxSel) {
    return `${n} submissions available. Select up to ${n} and choose who will conduct each interview.`;
  }
  if (n >= INTERVIEW_SCHEDULE_MAX_CHOICE_ROWS) {
    return `Select up to ${maxSel} submissions and choose who will conduct each interview. Showing up to ${INTERVIEW_SCHEDULE_MAX_CHOICE_ROWS}.`;
  }
  return `Select up to ${maxSel} of ${n} available submissions and choose who will conduct each interview.`;
}

/**
 * Task-graph wait / reply copy for Voice AI choice.
 * When fewer than the max selection cap are available, state the real count.
 */
export function formatVoiceAiChoiceQuestion(availableCount: number): string {
  const n = Math.max(0, Math.floor(availableCount));
  const maxSel = VOICE_AI_MAX_SELECTIONS;
  if (n <= 0) {
    return 'No submissions are available for a Voice AI interview.';
  }
  if (n === 1) {
    return '1 submission available. Select it for the Voice AI interview, then continue.';
  }
  if (n < maxSel) {
    return `${n} submissions available. Select up to ${n} submission(s) for a Voice AI interview, then continue.`;
  }
  return `Multiple submissions match. Select up to ${maxSel} submission(s) to schedule a Voice AI interview for, then continue.`;
}

/** Right-panel subtitle for Voice AI choice. */
export function formatVoiceAiChoicePanelSubtitle(availableCount: number): string {
  const n = Math.max(0, Math.floor(availableCount));
  const maxSel = VOICE_AI_MAX_SELECTIONS;
  if (n <= 0) {
    return 'No submissions available.';
  }
  if (n === 1) {
    return '1 submission available. Select it for one shared Voice AI interview draft.';
  }
  if (n < maxSel) {
    return `${n} submissions available. Select up to ${n} for one shared Voice AI interview draft.`;
  }
  if (n >= VOICE_AI_MAX_CHOICE_ROWS) {
    return `Select up to ${maxSel} submissions for one shared Voice AI interview draft. Showing up to ${VOICE_AI_MAX_CHOICE_ROWS}.`;
  }
  return `Select up to ${maxSel} of ${n} available submissions for one shared Voice AI interview draft.`;
}

export const SUBMISSION_CHOICE_WAITING_FOR = 'submission_choice';

export const INTERVIEW_SCHEDULE_CHOICE_WAITING_FOR = 'interview_schedule_choice';

export const VOICE_AI_CHOICE_WAITING_FOR = 'voice_ai_choice';

export const SUBMISSION_CHOICE_QUESTION = 'Choose candidate & job to submission';

/** ATS submissions module id for mandatory-field configuration. */
export const SUBMISSION_MANDATORY_MODULE_ID = 19;

/** Shared secret for ATS AiAgents S2S calls (matches domain orch / ATS PHP). */
export const SUBMISSION_ATS_INTERNAL_TOKEN = 'DYTD7OVJTEeQm4ZOj82aGg1zy7VseSeml36r3u';

/** ATS getMandatoryFields field_type values. */
export type SubmissionMandatoryFieldType =
  | 'textfield'
  | 'textarea'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'radio'
  | 'date'
  | 'multifields'
  /** Multi email chips (ATS `field_type: email` / `add_externals_emailids`). */
  | 'emailchips';

/** Max external email chips for `add_externals_emailids`. */
export const SUBMISSION_EXTERNAL_EMAILS_MAX = 5;

/**
 * Normalize raw chip / string / CSV input into trimmed unique emails (order preserved).
 * Does not validate format — use {@link validateSubmissionExternalEmails}.
 */
export function coerceSubmissionExternalEmails(raw: unknown): string[] {
  const tokens: string[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') {
        tokens.push(...item.split(/[,;]+/));
      } else if (item != null) {
        tokens.push(String(item));
      }
    }
  } else if (typeof raw === 'string') {
    tokens.push(...raw.split(/[,;]+/));
  } else if (raw != null) {
    tokens.push(String(raw));
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of tokens) {
    const email = token.trim();
    if (!email) {
      continue;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(email);
  }
  return out;
}

/** True when value looks like an email (aligned with send-mail client check). */
export function isSubmissionExternalEmailValid(raw: string): boolean {
  const addr = (raw ?? '').trim();
  if (!addr || addr.length > 254 || /\s/.test(addr)) {
    return false;
  }
  const at = addr.lastIndexOf('@');
  if (at <= 0 || at === addr.length - 1) {
    return false;
  }
  const domain = addr.slice(at + 1);
  if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
    return false;
  }
  const tld = domain.slice(domain.lastIndexOf('.') + 1);
  if (!tld || tld.length < 2) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(addr);
}

/**
 * Sanitize chip list: keep valid emails only, dedupe, cap at max.
 * Returns `{ emails, rejected, truncated }` for UI feedback.
 */
export function sanitizeSubmissionExternalEmails(
  raw: unknown,
  max = SUBMISSION_EXTERNAL_EMAILS_MAX,
): { emails: string[]; rejected: string[]; truncated: boolean } {
  const tokens = coerceSubmissionExternalEmails(raw);
  const emails: string[] = [];
  const rejected: string[] = [];
  for (const token of tokens) {
    if (isSubmissionExternalEmailValid(token)) {
      emails.push(token);
    } else {
      rejected.push(token);
    }
  }
  const truncated = emails.length > max;
  return {
    emails: truncated ? emails.slice(0, max) : emails,
    rejected,
    truncated,
  };
}

/** Validate mandatory external emails (required when field is present as mandatory). */
export function validateSubmissionExternalEmails(raw: unknown): string | null {
  const { emails, rejected, truncated } = sanitizeSubmissionExternalEmails(raw);
  if (rejected.length) {
    return 'Enter valid email addresses only.';
  }
  if (truncated || emails.length > SUBMISSION_EXTERNAL_EMAILS_MAX) {
    return `You can add up to ${SUBMISSION_EXTERNAL_EMAILS_MAX} email addresses.`;
  }
  if (!emails.length) {
    return 'Please enter at least one email address.';
  }
  return null;
}

/** ATS stores `add_externals_emailids` as a comma-separated string. */
export function serializeSubmissionExternalEmails(raw: unknown): string {
  return sanitizeSubmissionExternalEmails(raw).emails.join(',');
}

export interface SubmissionMandatoryFieldOption {
  value: string;
  label: string;
}

export interface SubmissionMandatoryFieldDefinition {
  fieldKey: string;
  label: string;
  sectionSlug: string;
  fieldType: SubmissionMandatoryFieldType;
  options?: SubmissionMandatoryFieldOption[];
  fieldValidation?: string | null;
  /** Present when field_type is multifields (e.g. Pay Rate / Bill Rate groups). */
  subFields?: SubmissionMandatoryFieldDefinition[];
}

/** Raw ATS field node (label + type + optional options / sub_fields). */
export type AtsMandatoryFieldNode =
  | string
  | {
      label_name?: string;
      field_type?: string;
      field_validation?: string;
      options?: Record<string, string> | Array<{ value?: string; label?: string }>;
      sub_fields?: Record<string, AtsMandatoryFieldNode>;
    };

export interface GetMandatoryFieldsRequest {
  business_unit_id: string;
  module_id: number;
  th_company_id: number;
  th_db_type: number;
  internal_token: string;
}

export interface GetMandatoryFieldsResponse {
  status: number;
  message?: string;
  business_unit_id?: string;
  all_mandatory_field_details?: Record<
    string,
    Record<string, Record<string, AtsMandatoryFieldNode>>
  >;
  /**
   * Per job_seeker_id map of verified eForm id → label.
   * Used to populate the `eform_ids` multiselect for that applicant's wizard step.
   */
  verified_eform_options?: Record<string, Record<string, string>>;
  /**
   * Per job_posting_id map of recipient user id → label.
   * Pre-selects those values on the `recipients` multiselect for that job's wizard step.
   */
  selected_recipients_options?: Record<string, Record<string, string>>;
  /**
   * Flat map of additional-notifier user id → label for the `additional_notifiers` multiselect.
   * Shape: `{ "U_490585": "Veerendra (email)", ... }`.
   */
  additional_notifiers_options?: Record<string, string>;
  /**
   * Preselected additional notifiers — flat map, or job/pipeline keyed nested map:
   * `{ "<job_or_pipeline_id>": { "U_…": "label", ... }, ... }`.
   */
  selected_additional_notifiers_options?:
    | Record<string, string>
    | Record<string, Record<string, string>>;
  /**
   * Per job_seeker_id map of resume document id → label (includes `new` = Upload new Resume).
   * Used to populate the `select_resume` dropdown for that applicant's wizard step.
   */
  resume_options?: Record<string, Record<string, string>>;
  /**
   * Preselected resume — keyed by job_seeker_id, and/or job_posting_id / pipeline_id.
   */
  selected_resume_options?: Record<string, Record<string, string>>;
  /**
   * Pay Rate (`bill_rate` multifield) defaults keyed by job_posting_id
   * (and pipeline_id when converting pipeline → submission).
   * Maps onto sub-keys: currency_code, bill_rate, tax_term, employmnet_type.
   */
  selected_currency_codes?: Record<string, string | number>;
  selected_bill_rates?: Record<string, string | number>;
  selected_tax_terms?: Record<string, string | number>;
  selected_employmnet_types?: Record<string, string | number>;
  /**
   * Bill Rate (`submission_billrate` multifield) defaults keyed by job_posting_id
   * (and pipeline_id when converting pipeline → submission).
   * Maps onto: billrate_currency_code, submission_billrate, billrate_tax_term,
   * billrate_employmnet_type.
   */
  selected_billrate_currency_codes?: Record<string, string | number>;
  selected_submission_billrates?: Record<string, string | number>;
  selected_billrate_tax_terms?: Record<string, string | number>;
  selected_billrate_employmnet_types?: Record<string, string | number>;
  /**
   * Shared dropdown option maps for Pay Rate / Bill Rate sub-fields.
   * - currency_code + billrate_currency_code ← currency_code_options
   * - tax_term + billrate_tax_term ← tax_term_options
   * - employmnet_type + billrate_employmnet_type ← employment_type_options
   */
  currency_code_options?: Record<string, string>;
  tax_term_options?: Record<string, string>;
  employment_type_options?: Record<string, string>;
  /** Pipeline convert extras (optional). */
  selected_date_of_available?: Record<string, string | number>;
  selected_video_links?: Record<string, string | number>;
  selected_relocate?: Record<string, string | number>;
  selected_add_externals_emailids?: Record<string, string | number>;
  selected_eform_options?: Record<string, Record<string, string>> | unknown;
  selected_submission_comments?: Record<string, string | number>;
  /**
   * Per pipeline_id (or job id) map of selected_* response keys marked readonly.
   * Example: `{ "370": { "selected_currency_codes": "readonly", "selected_resume_options": "readonly" } }`.
   */
  readonly_mandatory_fields?: Record<string, Record<string, string>>;
  /** Comma-separated pipeline row PKs when converting pipeline → submission. */
  pipeline_ids?: string;
}

export interface SubmissionMandatoryFieldsByPair {
  job_posting_id: string;
  job_seeker_id: string;
  fields: Record<string, unknown>;
  skipped?: boolean;
}

export interface SubmissionChoiceSubmitPayload {
  selections: { job_posting_id: string; job_seeker_id: string }[];
  mandatory_fields_by_pair?: SubmissionMandatoryFieldsByPair[];
}

/** Paused interview scheduling after Ask Anything / prior submission list. */
export const INTERVIEW_SCHEDULE_CHOICE_OUTPUT_TYPE = 'interview_schedule_choice_needed';

/** Paused Voice AI after multiple submissions — pick up to 5 for one shared draft. */
export const VOICE_AI_CHOICE_OUTPUT_TYPE = 'voice_ai_choice_needed';

export type SubmissionChoicePanelMode =
  | 'submission'
  | 'pipeline'
  | 'interview_schedule'
  | 'voice_ai';

export type InterviewSchedulerRoleKey =
  | 'primary_recruiter'
  | 'submitted_by'
  | 'hiring_manager';

export const INTERVIEW_SCHEDULER_ROLE_OPTIONS: {
  key: InterviewSchedulerRoleKey;
  label: string;
}[] = [
  { key: 'primary_recruiter', label: 'Primary Recruiter' },
  { key: 'submitted_by', label: 'Submission created by' },
  { key: 'hiring_manager', label: 'Recruitment Manager' },
];

/** Per-row picker label (task-graph panel + direct interview choice panel). */
export const INTERVIEW_SCHEDULER_ROLE_QUESTION =
  "Choose who'll be conducting this interview. We'll automatically pull their open calendar slots and send them to the candidate, who can then pick a time that suits them best.";

/** Bold note shown after {@link INTERVIEW_SCHEDULER_ROLE_QUESTION}. */
export const INTERVIEW_SCHEDULER_ROLE_NOTE =
  "Note: the selected person's calendar must be integrated for this to work.";

export interface SubmissionChoicePair {
  jobPostingId: string;
  jobSeekerId: string;
  applicantName: string;
  jobCode: string;
  jobTitle: string;
  /** Match score 0–100 when available from upstream ranker / lookup. */
  matchScore: number | null;
  /**
   * `job_seeker_applied_jobs.id` (row PK). Used for interview / Voice AI API selections.
   * Do not show this in the UI — prefer {@link submissionDisplayId}.
   */
  submissionId?: string | null;
  /**
   * Existing pipeline row PK (`applied_status = -2`) when this pair will convert
   * to a submission. Sent to getMandatoryFields as `pipeline_ids`.
   */
  pipelineId?: string | null;
  /**
   * Recruiter-facing `job_seeker_applied_jobs.submission_id` (display code).
   * From lookup_rows `submission_display_id` when Domain Orch provides it.
   */
  submissionDisplayId?: string | null;
  submissionStatus?: string | null;
  applicantEmail?: string | null;
  applicantMobile?: string | null;
  applicantLocation?: string | null;
  /**
   * Applicant primary skill (`job_seekers.primary_skill`), or resume ranker
   * `primary_job_title` when skill is absent. Shown under the name; missing values render as N/A.
   */
  primarySkill?: string | null;
  recruiterName?: string | null;
  /** Domain Orch pre-check: pair already exists in ATS for this job. */
  alreadySubmitted?: boolean;
  /** Encrypted job seeker / applicant id for ATS applicant deep links + resume preview. */
  applicantEncryptedId?: string | null;
  /** Encrypted job posting id for ATS job deep links. */
  jobEncryptedId?: string | null;
  /** Encrypted applied-job PK for ATS submission deep links. */
  submissionEncryptedId?: string | null;
  /** Job business unit — required for submission mandatory-field lookup. */
  businessUnitId?: string | null;
}

export interface SubmissionChoiceComposePayload {
  task: TaskGraphTaskDto;
  sourceMessage: AgenticAiMessage;
  mode?: SubmissionChoicePanelMode;
}

/** Tenant context from a direct `*_choice_needed` result (single-agent, no task graph). */
export interface ChoiceActionContext {
  th_domain: string;
  th_company_id: number;
  th_user_id: number;
  th_db_type: number;
  /** Recruiter email for ATS (submission/pipeline); optional for interview/voice. */
  user_email_id?: string;
  /** Voice AI: call type / schedule prefills from the original request. */
  draft_prefs?: {
    interviewType?: number;
    callMode?: string;
    enableVideoCalling?: boolean;
    scheduleIt?: boolean;
    scheduledAt?: string;
    timezoneId?: string;
  };
}

export interface SubmissionChoiceContext {
  conversationId: string;
  conversationMessageId: string | null;
  taskGraphNodeId: number | null;
  taskGraphId?: string | null;
  resumeToken?: string | null;
  task: TaskGraphTaskDto;
  pairs: SubmissionChoicePair[];
  /** submission | pipeline | interview_schedule | voice_ai */
  mode?: SubmissionChoicePanelMode;
  /**
   * Direct (non–task-graph) completion: pass action_context + lookup_rows to complete-choice.
   * When set, taskGraphId / resumeToken are optional.
   */
  actionContext?: ChoiceActionContext | null;
  lookupRows?: Array<Record<string, unknown>> | null;
}

/** True when choice panel / wait is for pipeline (tag) agent, not submission. */
export function isPipelineChoiceMode(
  mode: SubmissionChoicePanelMode | null | undefined,
  toolName?: string | null,
): boolean {
  if (mode === 'pipeline') {
    return true;
  }
  const t = (toolName ?? '').toString().trim().toLowerCase();
  return t === 'pipeline_agent' || t === 'tag_agent';
}

export interface CompleteSubmissionChoiceRequest {
  /** Task-graph wait path. */
  taskGraphId?: string;
  taskId?: string;
  resumeToken?: string;
  /** Direct single-agent path (no task graph). */
  action_context?: ChoiceActionContext;
  lookup_rows?: Array<Record<string, unknown>>;
  /** Encrypted conversation id — persist result row after direct complete-choice. */
  conversationId?: string;
  selections: { job_posting_id: string; job_seeker_id: string }[];
  /** Per pair mandatory submission fields captured in the choice-panel wizard. */
  mandatory_fields_by_pair?: SubmissionMandatoryFieldsByPair[];
}

export interface CompleteSubmissionChoiceResult {
  ok: boolean;
  message?: string;
  summary?: string;
  taskGraphSync?: { ok: boolean; reason?: string };
  result?: Record<string, unknown>;
}

export interface CompleteInterviewScheduleChoiceRequest {
  /** Task-graph wait path. */
  taskGraphId?: string;
  taskId?: string;
  resumeToken?: string;
  /** Direct single-agent path (no task graph). */
  action_context?: ChoiceActionContext;
  lookup_rows?: Array<Record<string, unknown>>;
  /** Encrypted conversation id — persist result row after direct complete-choice. */
  conversationId?: string;
  selections: { submission_id: string; scheduled_by: InterviewSchedulerRoleKey }[];
}

export interface CompleteInterviewScheduleChoiceResult {
  ok: boolean;
  message?: string;
  summary?: string;
  taskGraphSync?: { ok: boolean; reason?: string };
  result?: Record<string, unknown>;
}

export interface CompleteVoiceAiChoiceRequest {
  taskGraphId?: string;
  taskId?: string;
  resumeToken?: string;
  action_context?: ChoiceActionContext;
  lookup_rows?: Array<Record<string, unknown>>;
  /** Encrypted conversation id — update choice row to draft after direct complete-choice. */
  conversationId?: string;
  /** Encrypted choice_needed assistant message id (direct mode). */
  conversationMessageId?: string;
  selections: { submission_id: string }[];
}

export interface CompleteVoiceAiChoiceResult {
  ok: boolean;
  message?: string;
  taskGraphSync?: { ok: boolean; reason?: string; resumeToken?: string };
  result?: Record<string, unknown>;
}

export type CandidateMatchingChoiceKind =
  | 'submission_choice'
  | 'pipeline_choice'
  | 'interview_schedule_choice'
  | 'voice_ai_choice';

/** Resolve choice payload from BFF `result` / assistant metadata (direct or nested). */
export function extractCandidateMatchingChoicePayload(
  source: Record<string, unknown> | null | undefined,
): {
  kind: CandidateMatchingChoiceKind;
  output: Record<string, unknown>;
} | null {
  if (!source) {
    return null;
  }
  const nested = asRecord(source['result']);
  if (nested) {
    const nestedKind = classifyChoiceNeededType(nested['type'] ?? nested['resultKind']);
    if (nestedKind) {
      return { kind: nestedKind, output: nested };
    }
  }
  const direct = classifyChoiceNeededType(source['type'] ?? source['resultKind']);
  if (!direct) {
    return null;
  }
  // Envelope typed as choice_needed but rows live under nested `result`.
  if (nested && Array.isArray(nested['lookup_rows'])) {
    return { kind: direct, output: nested };
  }
  return { kind: direct, output: source };
}

function classifyChoiceNeededType(raw: unknown): CandidateMatchingChoiceKind | null {
  const t = asTrimmedString(raw).toLowerCase();
  if (t === 'submission_choice_needed') {
    return 'submission_choice';
  }
  if (t === 'pipeline_choice_needed') {
    return 'pipeline_choice';
  }
  if (t === INTERVIEW_SCHEDULE_CHOICE_OUTPUT_TYPE || t === 'interview_schedule_choice') {
    return 'interview_schedule_choice';
  }
  if (t === VOICE_AI_CHOICE_OUTPUT_TYPE || t === 'voice_ai_choice') {
    return 'voice_ai_choice';
  }
  return null;
}

export function parseChoiceActionContext(raw: unknown): ChoiceActionContext | null {
  const o = asRecord(raw);
  if (!o) {
    return null;
  }
  const thDomain = asTrimmedString(o['th_domain']);
  const thCompanyId = Number(o['th_company_id']);
  const thUserId = Number(o['th_user_id']);
  const thDbType = Number(o['th_db_type']);
  const userEmailId = asTrimmedString(o['user_email_id']);
  if (
    !thDomain ||
    !Number.isFinite(thCompanyId) ||
    thCompanyId <= 0 ||
    !Number.isFinite(thUserId) ||
    thUserId <= 0
  ) {
    return null;
  }
  const draftPrefsRaw = asRecord(o['draft_prefs']) ?? asRecord(o['draftPrefs']);
  let draft_prefs: ChoiceActionContext['draft_prefs'] | undefined;
  if (draftPrefsRaw) {
    const interviewTypeRaw = Number(
      draftPrefsRaw['interviewType'] ?? draftPrefsRaw['interview_type'],
    );
    const interviewType =
      interviewTypeRaw === 1 || interviewTypeRaw === 2 || interviewTypeRaw === 3
        ? interviewTypeRaw
        : undefined;
    const callMode =
      asTrimmedString(draftPrefsRaw['callMode']) ||
      asTrimmedString(draftPrefsRaw['call_mode']) ||
      undefined;
    const enableVideoCalling =
      typeof draftPrefsRaw['enableVideoCalling'] === 'boolean'
        ? draftPrefsRaw['enableVideoCalling']
        : typeof draftPrefsRaw['enable_video_calling'] === 'boolean'
          ? draftPrefsRaw['enable_video_calling']
          : undefined;
    const scheduleIt =
      typeof draftPrefsRaw['scheduleIt'] === 'boolean'
        ? draftPrefsRaw['scheduleIt']
        : typeof draftPrefsRaw['schedule_it'] === 'boolean'
          ? draftPrefsRaw['schedule_it']
          : undefined;
    const scheduledAt =
      asTrimmedString(draftPrefsRaw['scheduledAt']) ||
      asTrimmedString(draftPrefsRaw['scheduled_at']) ||
      undefined;
    const timezoneId =
      asTrimmedString(draftPrefsRaw['timezoneId']) ||
      asTrimmedString(draftPrefsRaw['timezone_id']) ||
      undefined;
    if (
      interviewType != null ||
      callMode ||
      enableVideoCalling != null ||
      scheduleIt != null ||
      scheduledAt ||
      timezoneId
    ) {
      draft_prefs = {
        ...(interviewType != null ? { interviewType } : {}),
        ...(callMode ? { callMode } : {}),
        ...(enableVideoCalling != null ? { enableVideoCalling } : {}),
        ...(scheduleIt != null ? { scheduleIt } : {}),
        ...(scheduledAt ? { scheduledAt } : {}),
        ...(timezoneId ? { timezoneId } : {}),
      };
    }
  }
  return {
    th_domain: thDomain,
    th_company_id: Math.floor(thCompanyId),
    th_user_id: Math.floor(thUserId),
    th_db_type: Number.isFinite(thDbType) && thDbType >= 0 ? Math.floor(thDbType) : 1,
    ...(userEmailId ? { user_email_id: userEmailId } : {}),
    ...(draft_prefs ? { draft_prefs } : {}),
  };
}

export function parseLookupRowsFromChoiceOutput(
  output: Record<string, unknown>,
): Array<Record<string, unknown>> {
  const raw = output['lookup_rows'];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (item): item is Record<string, unknown> =>
      item != null && typeof item === 'object' && !Array.isArray(item),
  );
}

/** Build a synthetic task so existing pair parsers / panel open path can reuse task-graph helpers. */
export function syntheticChoiceTaskFromOutput(
  output: Record<string, unknown>,
  kind: CandidateMatchingChoiceKind,
): TaskGraphTaskDto {
  const toolName =
    kind === 'interview_schedule_choice'
      ? 'interview_scheduling_agent'
      : kind === 'voice_ai_choice'
        ? 'voice_ai_call_agent'
        : kind === 'pipeline_choice'
          ? 'pipeline_agent'
          : 'submission_agent';
  return {
    taskId: 'direct',
    name: toolName,
    toolName,
    status: 'waiting_user_action',
    waitingFor:
      kind === 'interview_schedule_choice'
        ? INTERVIEW_SCHEDULE_CHOICE_WAITING_FOR
        : kind === 'voice_ai_choice'
          ? VOICE_AI_CHOICE_WAITING_FOR
          : SUBMISSION_CHOICE_WAITING_FOR,
    output,
  } as TaskGraphTaskDto;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v != null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function asTrimmedString(v: unknown): string {
  if (typeof v === 'string') {
    return v.trim();
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return String(v);
  }
  return '';
}

/**
 * Primary skill from BQ / job_seekers, else resume ranker primary job title.
 * Used by submission, pipeline, and interview choice panels.
 */
function readBusinessUnitIdFromChoiceRow(row: Record<string, unknown>): string | null {
  return (
    asTrimmedString(
      row['business_unit_id'] ??
        row['businessUnitId'] ??
        row['business_unit'] ??
        row['bu_id'],
    ) || null
  );
}

function normalizeMandatoryFieldType(
  raw: string | null | undefined,
  fieldKey: string,
): SubmissionMandatoryFieldType {
  const t = (raw ?? '').trim().toLowerCase();
  const key = (fieldKey ?? '').trim().toLowerCase();
  if (t === 'textarea' || t === 'text_area') {
    return 'textarea';
  }
  if (t === 'number' || t === 'numeric') {
    return 'number';
  }
  if (t === 'select' || t === 'dropdown') {
    return 'select';
  }
  if (t === 'multiselect' || t === 'multi_select' || t === 'multi-select') {
    return 'multiselect';
  }
  if (t === 'radio' || t === 'radiobutton') {
    return 'radio';
  }
  if (t === 'date' || t === 'datepicker' || t === 'calendar') {
    return 'date';
  }
  if (t === 'multifields' || t === 'multi_fields' || t === 'group') {
    return 'multifields';
  }
  if (t === 'email' || t === 'emailchips' || t === 'chips' || key === 'add_externals_emailids') {
    return 'emailchips';
  }
  if (key === 'submission_comments') {
    return 'textarea';
  }
  return 'textfield';
}

function parseMandatoryFieldOptions(
  raw: unknown,
): SubmissionMandatoryFieldOption[] | undefined {
  if (raw == null) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    const out: SubmissionMandatoryFieldOption[] = [];
    for (const item of raw) {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) {
        continue;
      }
      const rec = item as Record<string, unknown>;
      const value = asTrimmedString(rec['value'] ?? rec['id'] ?? rec['code']);
      const label = asTrimmedString(rec['label'] ?? rec['name'] ?? rec['label_name']) || value;
      if (!value) {
        continue;
      }
      out.push({ value, label });
    }
    return out.length > 0 ? out : undefined;
  }
  if (typeof raw === 'object') {
    const out: SubmissionMandatoryFieldOption[] = [];
    for (const [value, label] of Object.entries(raw as Record<string, unknown>)) {
      const v = (value ?? '').trim();
      if (!v) {
        continue;
      }
      out.push({
        value: v,
        label: asTrimmedString(label) || v,
      });
    }
    return out.length > 0 ? out : undefined;
  }
  return undefined;
}

function parseMandatoryFieldNode(
  fieldKey: string,
  node: AtsMandatoryFieldNode,
  sectionSlug: string,
): SubmissionMandatoryFieldDefinition | null {
  const key = (fieldKey ?? '').trim();
  if (!key) {
    return null;
  }
  // Legacy shape: fieldKey → "Label"
  if (typeof node === 'string') {
    const keyLower = key.toLowerCase();
    let fieldType = normalizeMandatoryFieldType(null, key);
    if (keyLower === 'relocate') {
      fieldType = 'radio';
    } else if (keyLower === 'select_resume') {
      fieldType = 'select';
    } else if (
      keyLower === 'eform_ids' ||
      keyLower === 'recipients' ||
      keyLower === 'additional_notifiers'
    ) {
      fieldType = 'multiselect';
    }
    return {
      fieldKey: key,
      label: node.trim() || key,
      sectionSlug,
      fieldType,
      ...(fieldType === 'radio' && keyLower === 'relocate'
        ? {
            options: [
              { value: '1', label: 'Yes' },
              { value: '2', label: 'No' },
            ],
          }
        : {}),
    };
  }
  if (node == null || typeof node !== 'object' || Array.isArray(node)) {
    return null;
  }
  const rec = node as Record<string, unknown>;
  const label =
    asTrimmedString(node.label_name) ||
    asTrimmedString(rec['labelName']) ||
    asTrimmedString(rec['label']) ||
    key;
  const rawType = asTrimmedString(node.field_type) || asTrimmedString(rec['fieldType']);
  let fieldType = normalizeMandatoryFieldType(rawType, key);
  const options = parseMandatoryFieldOptions(node.options ?? rec['Options']);
  const fieldValidation =
    asTrimmedString(node.field_validation) ||
    asTrimmedString(rec['fieldValidation']) ||
    null;
  const rawSub = node.sub_fields ?? rec['subFields'] ?? rec['subfields'];
  let subFields: SubmissionMandatoryFieldDefinition[] | undefined;
  if (rawSub && typeof rawSub === 'object' && !Array.isArray(rawSub)) {
    subFields = [];
    for (const [subKey, subNode] of Object.entries(rawSub as Record<string, AtsMandatoryFieldNode>)) {
      const parsed = parseMandatoryFieldNode(subKey, subNode, sectionSlug);
      if (parsed) {
        subFields.push(parsed);
      }
    }
    if (subFields.length === 0) {
      subFields = undefined;
    }
  }
  // Known submission-form field keys — enforce control type even if ATS omits field_type.
  const keyLower = key.toLowerCase();
  if (subFields?.length || keyLower === 'bill_rate' || keyLower === 'submission_billrate') {
    if (subFields?.length) {
      fieldType = 'multifields';
    }
  } else if (keyLower === 'relocate') {
    fieldType = 'radio';
  } else if (keyLower === 'select_resume') {
    fieldType = 'select';
  } else if (
    keyLower === 'eform_ids' ||
    keyLower === 'recipients' ||
    keyLower === 'additional_notifiers'
  ) {
    fieldType = 'multiselect';
  } else if (keyLower === 'submission_comments') {
    fieldType = 'textarea';
  }

  if (keyLower === 'relocate' && (!options || options.length === 0)) {
    return {
      fieldKey: key,
      label,
      sectionSlug,
      fieldType: 'radio',
      options: [
        { value: '1', label: 'Yes' },
        { value: '2', label: 'No' },
      ],
      ...(fieldValidation ? { fieldValidation } : {}),
    };
  }

  return {
    fieldKey: key,
    label,
    sectionSlug,
    fieldType: subFields?.length ? 'multifields' : fieldType,
    ...(options ? { options } : {}),
    ...(fieldValidation ? { fieldValidation } : {}),
    ...(subFields ? { subFields } : {}),
  };
}

/** Normalize API payload into a BU → section → fields catalog. */
export function coerceMandatoryFieldCatalog(
  raw: unknown,
): Record<string, Record<string, Record<string, AtsMandatoryFieldNode>>> {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const root = value as Record<string, unknown>;
  // Accidental double-wrap from some proxies.
  if (
    root['all_mandatory_field_details'] != null &&
    typeof root['all_mandatory_field_details'] === 'object'
  ) {
    return coerceMandatoryFieldCatalog(root['all_mandatory_field_details']);
  }

  const out: Record<string, Record<string, Record<string, AtsMandatoryFieldNode>>> = {};
  for (const [buKey, buVal] of Object.entries(root)) {
    let buData: unknown = buVal;
    if (typeof buData === 'string') {
      try {
        buData = JSON.parse(buData) as unknown;
      } catch {
        continue;
      }
    }
    if (buData == null || typeof buData !== 'object' || Array.isArray(buData)) {
      continue;
    }
    const sectionsOut: Record<string, Record<string, AtsMandatoryFieldNode>> = {};
    for (const [sectionKey, sectionVal] of Object.entries(
      buData as Record<string, unknown>,
    )) {
      let sectionData: unknown = sectionVal;
      if (typeof sectionData === 'string') {
        try {
          sectionData = JSON.parse(sectionData) as unknown;
        } catch {
          continue;
        }
      }
      if (
        sectionData == null ||
        typeof sectionData !== 'object' ||
        Array.isArray(sectionData)
      ) {
        continue;
      }
      const fieldsOut: Record<string, AtsMandatoryFieldNode> = {};
      for (const [fieldKey, fieldVal] of Object.entries(
        sectionData as Record<string, unknown>,
      )) {
        let node: unknown = fieldVal;
        if (typeof node === 'string') {
          const trimmed = node.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
              node = JSON.parse(trimmed) as unknown;
            } catch {
              // keep plain label string
            }
          }
        }
        fieldsOut[fieldKey] = node as AtsMandatoryFieldNode;
      }
      sectionsOut[sectionKey] = fieldsOut;
    }
    out[buKey] = sectionsOut;
  }
  return out;
}

/** Flatten ATS mandatory-field config for one business unit into form rows. */
export function flattenMandatoryFieldsForBusinessUnit(
  allDetails:
    | Record<string, Record<string, Record<string, AtsMandatoryFieldNode>>>
    | null
    | undefined,
  businessUnitId: string,
): SubmissionMandatoryFieldDefinition[] {
  if (!allDetails || typeof allDetails !== 'object') {
    return [];
  }
  const buKey = (businessUnitId ?? '').trim();
  let buData =
    (buKey ? allDetails[buKey] : undefined) ??
    (buKey ? allDetails[String(Number(buKey))] : undefined);
  // When BU is missing / mismatched, use the only catalog entry if unique.
  if (!buData) {
    const keys = Object.keys(allDetails);
    if (keys.length === 1) {
      buData = allDetails[keys[0]!];
    }
  }
  if (!buData || typeof buData !== 'object') {
    return [];
  }
  const out: SubmissionMandatoryFieldDefinition[] = [];
  for (const [sectionSlug, fields] of Object.entries(buData)) {
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      continue;
    }
    const section = (sectionSlug ?? '').trim() || 'standard_fields';
    for (const [fieldKey, node] of Object.entries(fields)) {
      const parsed = parseMandatoryFieldNode(fieldKey, node as AtsMandatoryFieldNode, section);
      if (parsed) {
        out.push(parsed);
      }
    }
  }
  return out;
}

/** job_seeker_id → verified eForm options for the `eform_ids` multiselect. */
export type VerifiedEformOptionsBySeeker = Record<string, SubmissionMandatoryFieldOption[]>;

/** job_posting_id → preselected recipient options for the `recipients` multiselect. */
export type SelectedRecipientsByJob = Record<string, SubmissionMandatoryFieldOption[]>;

/** job_posting_id → scalar default (pay/bill rate multifield sub-values). */
export type SelectedScalarByJob = Record<string, string>;

/** Bundled job-keyed defaults for Pay Rate (`bill_rate`) + Bill Rate (`submission_billrate`). */
export type SelectedPayBillRateDefaultsByJob = {
  currencyCodes: SelectedScalarByJob;
  billRates: SelectedScalarByJob;
  taxTerms: SelectedScalarByJob;
  employmentTypes: SelectedScalarByJob;
  billrateCurrencyCodes: SelectedScalarByJob;
  submissionBillrates: SelectedScalarByJob;
  billrateTaxTerms: SelectedScalarByJob;
  billrateEmploymentTypes: SelectedScalarByJob;
};

function entityIdLookupKeys(entityId: string | number | null | undefined): string[] {
  const id = String(entityId ?? '').trim();
  if (!id) {
    return [];
  }
  const keys = new Set<string>([id]);
  const n = Number(id);
  if (Number.isFinite(n) && n > 0) {
    keys.add(String(n));
    keys.add(String(Math.trunc(n)));
  }
  return [...keys];
}

function coerceIdToOptionsMap(raw: unknown): Record<string, SubmissionMandatoryFieldOption[]> {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, SubmissionMandatoryFieldOption[]> = {};
  for (const [mapKey, entries] of Object.entries(value as Record<string, unknown>)) {
    const id = String(mapKey ?? '').trim();
    if (!id) {
      continue;
    }
    const options = parseMandatoryFieldOptions(entries);
    out[id] = options?.length ? options : [];
  }
  return out;
}

function optionsForEntityId(
  byId: Record<string, SubmissionMandatoryFieldOption[]> | null | undefined,
  entityId: string | number | null | undefined,
): SubmissionMandatoryFieldOption[] {
  if (!byId) {
    return [];
  }
  for (const key of entityIdLookupKeys(entityId)) {
    if (Object.prototype.hasOwnProperty.call(byId, key)) {
      return byId[key] ?? [];
    }
  }
  const want = Number(String(entityId ?? '').trim());
  if (Number.isFinite(want) && want > 0) {
    for (const [key, opts] of Object.entries(byId)) {
      const have = Number(key);
      if (Number.isFinite(have) && have === want) {
        return opts ?? [];
      }
    }
  }
  return [];
}

/**
 * Normalize ATS `verified_eform_options` into seeker-keyed option lists.
 * Shape: `{ "<job_seeker_id>": { "<eform_id>": "<label>", ... }, ... }`.
 */
export function coerceVerifiedEformOptions(raw: unknown): VerifiedEformOptionsBySeeker {
  return coerceIdToOptionsMap(raw);
}

/** Resolve verified eForm options for one applicant (tolerant of string/number id forms). */
export function verifiedEformOptionsForJobSeeker(
  bySeeker: VerifiedEformOptionsBySeeker | null | undefined,
  jobSeekerId: string,
): SubmissionMandatoryFieldOption[] {
  return optionsForEntityId(bySeeker, jobSeekerId);
}

/**
 * Attach per-applicant verified eForm options onto the `eform_ids` field.
 * Does not mutate the catalog — returns a new field list for the active wizard step.
 */
export function applyVerifiedEformOptionsToFields(
  fields: SubmissionMandatoryFieldDefinition[],
  jobSeekerId: string,
  bySeeker: VerifiedEformOptionsBySeeker | null | undefined,
): SubmissionMandatoryFieldDefinition[] {
  if (!fields.length) {
    return fields;
  }
  const eformOptions = verifiedEformOptionsForJobSeeker(bySeeker, jobSeekerId);
  let changed = false;
  const next = fields.map((field) => {
    if ((field.fieldKey ?? '').toLowerCase() !== 'eform_ids') {
      return field;
    }
    changed = true;
    return {
      ...field,
      fieldType: 'multiselect' as const,
      options: eformOptions,
    };
  });
  return changed ? next : fields;
}

/**
 * Normalize ATS `selected_recipients_options` into job-keyed option lists.
 * Shape: `{ "<job_posting_id>": { "<recipient_id>": "<label>", ... }, ... }`.
 */
export function coerceSelectedRecipientsOptions(raw: unknown): SelectedRecipientsByJob {
  return coerceIdToOptionsMap(raw);
}

/** Resolve preselected recipient options for one job posting. */
export function selectedRecipientOptionsForJob(
  byJob: SelectedRecipientsByJob | null | undefined,
  jobPostingId: string,
): SubmissionMandatoryFieldOption[] {
  return optionsForEntityId(byJob, jobPostingId);
}

/** Preselected recipient values (ids) for one job posting. */
export function selectedRecipientValuesForJob(
  byJob: SelectedRecipientsByJob | null | undefined,
  jobPostingId: string,
  pipelineId?: string | null,
): string[] {
  for (const id of [pipelineId, jobPostingId]) {
    const opts = selectedRecipientOptionsForJob(byJob, id ?? '');
    if (opts.length) {
      return opts.map((o) => String(o.value));
    }
  }
  return [];
}

/**
 * Merge job-specific selected recipient options into the `recipients` field options
 * so preselected ids always have labels in the multiselect.
 */
export function applySelectedRecipientOptionsToFields(
  fields: SubmissionMandatoryFieldDefinition[],
  jobPostingId: string,
  byJob: SelectedRecipientsByJob | null | undefined,
  pipelineId?: string | null,
): SubmissionMandatoryFieldDefinition[] {
  if (!fields.length) {
    return fields;
  }
  const pipelineOpts = selectedRecipientOptionsForJob(
    byJob,
    (pipelineId ?? '').trim(),
  );
  const selectedOpts = pipelineOpts.length
    ? pipelineOpts
    : selectedRecipientOptionsForJob(byJob, jobPostingId);
  if (!selectedOpts.length) {
    return fields;
  }
  let changed = false;
  const next = fields.map((field) => {
    if ((field.fieldKey ?? '').toLowerCase() !== 'recipients') {
      return field;
    }
    const existing = field.options ?? [];
    const byValue = new Map(existing.map((o) => [String(o.value), o]));
    for (const opt of selectedOpts) {
      if (!byValue.has(String(opt.value))) {
        byValue.set(String(opt.value), opt);
      }
    }
    changed = true;
    return {
      ...field,
      fieldType: 'multiselect' as const,
      options: [...byValue.values()],
    };
  });
  return changed ? next : fields;
}

/**
 * Normalize ATS flat option maps (`additional_notifiers_options` /
 * `selected_additional_notifiers_options`) into option lists.
 * Shape: `{ "<user_id>": "<label>", ... }`.
 */
export function coerceAdditionalNotifiersOptions(
  raw: unknown,
): SubmissionMandatoryFieldOption[] {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  // Nested job/pipeline keyed map → flatten unique options (legacy callers).
  const entries = Object.entries(value as Record<string, unknown>);
  if (
    entries.length > 0 &&
    entries.every(
      ([, v]) => v != null && typeof v === 'object' && !Array.isArray(v),
    )
  ) {
    const byValue = new Map<string, SubmissionMandatoryFieldOption>();
    for (const [, nested] of entries) {
      for (const opt of parseMandatoryFieldOptions(nested) ?? []) {
        byValue.set(String(opt.value), opt);
      }
    }
    return [...byValue.values()];
  }
  return parseMandatoryFieldOptions(value) ?? [];
}

/**
 * Coerce `selected_additional_notifiers_options` as a job/pipeline keyed map.
 * Also accepts legacy flat maps (stored under a synthetic empty-key bucket via
 * {@link selectedAdditionalNotifierValuesForPair}).
 */
export function coerceSelectedAdditionalNotifiersByJob(
  raw: unknown,
): SelectedRecipientsByJob {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    return {};
  }
  const nested = entries.every(
    ([, v]) => v != null && typeof v === 'object' && !Array.isArray(v),
  );
  if (nested) {
    return coerceIdToOptionsMap(value);
  }
  // Flat map → single bucket for all pairs.
  const flat = parseMandatoryFieldOptions(value) ?? [];
  return flat.length ? { '*': flat } : {};
}

/** Preselected additional-notifier values (ids) from a coerced option list. */
export function selectedAdditionalNotifierValues(
  options: SubmissionMandatoryFieldOption[] | null | undefined,
): string[] {
  return (options ?? []).map((o) => String(o.value));
}

/**
 * Resolve preselected additional-notifier options for a pair.
 * Tries pipeline_id → job_posting_id → flat `*` bucket.
 */
export function selectedAdditionalNotifierOptionsForPair(
  byJob: SelectedRecipientsByJob | null | undefined,
  jobPostingId: string | null | undefined,
  pipelineId?: string | null,
): SubmissionMandatoryFieldOption[] {
  for (const id of [pipelineId, jobPostingId, '*']) {
    const opts = optionsForEntityId(byJob, id);
    if (opts.length) {
      return opts;
    }
  }
  return [];
}

/**
 * Resolve preselected additional-notifier ids for a pair.
 * Tries pipeline_id → job_posting_id → flat `*` bucket.
 */
export function selectedAdditionalNotifierValuesForPair(
  byJob: SelectedRecipientsByJob | null | undefined,
  jobPostingId: string | null | undefined,
  pipelineId?: string | null,
): string[] {
  return selectedAdditionalNotifierOptionsForPair(byJob, jobPostingId, pipelineId).map(
    (o) => String(o.value),
  );
}

/** Shared Pay Rate / Bill Rate dropdown option maps from getMandatoryFields. */
export type PayBillRateDropdownOptions = {
  currencyCodeOptions: SubmissionMandatoryFieldOption[];
  taxTermOptions: SubmissionMandatoryFieldOption[];
  employmentTypeOptions: SubmissionMandatoryFieldOption[];
};

export function emptyPayBillRateDropdownOptions(): PayBillRateDropdownOptions {
  return {
    currencyCodeOptions: [],
    taxTermOptions: [],
    employmentTypeOptions: [],
  };
}

/**
 * ATS `readonly_mandatory_fields` uses selected_* response keys.
 * Map those onto form fieldKey / optional subFieldKey for the wizard UI.
 */
const READONLY_SELECTED_KEY_TO_FIELD: Record<
  string,
  { fieldKey: string; subKey?: string }
> = {
  selected_currency_codes: { fieldKey: 'bill_rate', subKey: 'currency_code' },
  selected_bill_rates: { fieldKey: 'bill_rate', subKey: 'bill_rate' },
  selected_tax_terms: { fieldKey: 'bill_rate', subKey: 'tax_term' },
  selected_employmnet_types: { fieldKey: 'bill_rate', subKey: 'employmnet_type' },
  selected_billrate_currency_codes: {
    fieldKey: 'submission_billrate',
    subKey: 'billrate_currency_code',
  },
  selected_submission_billrates: {
    fieldKey: 'submission_billrate',
    subKey: 'submission_billrate',
  },
  selected_billrate_tax_terms: {
    fieldKey: 'submission_billrate',
    subKey: 'billrate_tax_term',
  },
  selected_billrate_employmnet_types: {
    fieldKey: 'submission_billrate',
    subKey: 'billrate_employmnet_type',
  },
  selected_resume_options: { fieldKey: 'select_resume' },
  selected_recipients_options: { fieldKey: 'recipients' },
  selected_additional_notifiers_options: { fieldKey: 'additional_notifiers' },
  selected_date_of_available: { fieldKey: 'date_of_available' },
  selected_video_links: { fieldKey: 'video_link' },
  selected_relocate: { fieldKey: 'relocate' },
  selected_add_externals_emailids: { fieldKey: 'add_externals_emailids' },
  selected_eform_options: { fieldKey: 'eform_ids' },
  selected_submission_comments: { fieldKey: 'submission_comments' },
};

/** pipeline/job id → set of `fieldKey` or `fieldKey|subKey` marked readonly. */
export type ReadonlyMandatoryFieldsByEntity = Record<string, Set<string>>;

function readonlyTrackKey(fieldKey: string, subKey?: string): string {
  const fk = (fieldKey ?? '').trim().toLowerCase();
  const sk = (subKey ?? '').trim().toLowerCase();
  return sk ? `${fk}|${sk}` : fk;
}

/**
 * Coerce ATS `readonly_mandatory_fields` into field/subfield track keys per entity id.
 */
export function coerceReadonlyMandatoryFields(
  raw: unknown,
): ReadonlyMandatoryFieldsByEntity {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: ReadonlyMandatoryFieldsByEntity = {};
  for (const [entityId, flags] of Object.entries(value as Record<string, unknown>)) {
    const id = String(entityId ?? '').trim();
    if (!id || flags == null || typeof flags !== 'object' || Array.isArray(flags)) {
      continue;
    }
    const keys = new Set<string>();
    for (const [selectedKey, flag] of Object.entries(flags as Record<string, unknown>)) {
      const flagStr = String(flag ?? '')
        .trim()
        .toLowerCase();
      if (flagStr !== 'readonly' && flagStr !== '1' && flagStr !== 'true') {
        continue;
      }
      const mapped = READONLY_SELECTED_KEY_TO_FIELD[(selectedKey ?? '').trim().toLowerCase()];
      if (!mapped) {
        // Allow direct field keys as a fallback (e.g. `bill_rate`, `select_resume`).
        const direct = (selectedKey ?? '').trim().toLowerCase();
        if (direct) {
          keys.add(direct);
        }
        continue;
      }
      keys.add(readonlyTrackKey(mapped.fieldKey, mapped.subKey));
    }
    if (keys.size) {
      out[id] = keys;
    }
  }
  return out;
}

/** Resolve readonly track-key set for a pair (pipeline id first, then job id). */
export function readonlyMandatoryKeysForPair(
  byEntity: ReadonlyMandatoryFieldsByEntity | null | undefined,
  pipelineId?: string | null,
  jobPostingId?: string | null,
): Set<string> {
  if (!byEntity) {
    return new Set();
  }
  for (const id of [pipelineId, jobPostingId]) {
    for (const key of entityIdLookupKeys(id)) {
      const hit = byEntity[key];
      if (hit && hit.size > 0) {
        return hit;
      }
    }
  }
  return new Set();
}

export function isMandatoryFieldReadonlyInSet(
  readonlyKeys: Set<string> | null | undefined,
  fieldKey: string,
  subKey?: string,
): boolean {
  if (!readonlyKeys || readonlyKeys.size === 0) {
    return false;
  }
  const fk = (fieldKey ?? '').trim().toLowerCase();
  if (!fk) {
    return false;
  }
  if (subKey) {
    if (readonlyKeys.has(readonlyTrackKey(fk, subKey))) {
      return true;
    }
    // Whole multifield group locked (direct `bill_rate` / `submission_billrate` flag).
    return readonlyKeys.has(readonlyTrackKey(fk));
  }
  if (readonlyKeys.has(readonlyTrackKey(fk))) {
    return true;
  }
  // Alias used in some catalogs.
  if (fk === 'additional_notifications') {
    return readonlyKeys.has(readonlyTrackKey('additional_notifiers'));
  }
  if (fk === 'additional_notifiers') {
    return readonlyKeys.has(readonlyTrackKey('additional_notifications'));
  }
  return false;
}

/**
 * Coerce top-level ATS option maps used by Pay Rate / Bill Rate sub-fields.
 * Ignores any nested `options` previously embedded on sub_fields.
 */
export function coercePayBillRateDropdownOptions(raw?: {
  currency_code_options?: unknown;
  tax_term_options?: unknown;
  employment_type_options?: unknown;
} | null): PayBillRateDropdownOptions {
  return {
    currencyCodeOptions: parseMandatoryFieldOptions(raw?.currency_code_options) ?? [],
    taxTermOptions: parseMandatoryFieldOptions(raw?.tax_term_options) ?? [],
    employmentTypeOptions: parseMandatoryFieldOptions(raw?.employment_type_options) ?? [],
  };
}

const PAY_BILL_CURRENCY_SUB_KEYS = new Set(['currency_code', 'billrate_currency_code']);
const PAY_BILL_TAX_TERM_SUB_KEYS = new Set(['tax_term', 'billrate_tax_term']);
const PAY_BILL_EMPLOYMENT_SUB_KEYS = new Set([
  'employmnet_type',
  'employment_type',
  'billrate_employmnet_type',
  'billrate_employment_type',
]);

/**
 * Attach shared top-level option maps onto Pay Rate / Bill Rate multifield sub-fields.
 * Replaces any prior nested options on those sub-keys.
 */
export function applyPayBillRateDropdownOptionsToFields(
  fields: SubmissionMandatoryFieldDefinition[],
  dropdownOptions: PayBillRateDropdownOptions | null | undefined,
): SubmissionMandatoryFieldDefinition[] {
  if (!fields.length || !dropdownOptions) {
    return fields;
  }
  const currency = dropdownOptions.currencyCodeOptions ?? [];
  const taxTerm = dropdownOptions.taxTermOptions ?? [];
  const employment = dropdownOptions.employmentTypeOptions ?? [];
  if (!currency.length && !taxTerm.length && !employment.length) {
    return fields;
  }

  let changed = false;
  const next = fields.map((field) => {
    const parentKey = (field.fieldKey ?? '').toLowerCase();
    if (
      parentKey !== 'bill_rate' &&
      parentKey !== 'submission_billrate' &&
      !field.subFields?.length
    ) {
      return field;
    }
    if (!field.subFields?.length) {
      return field;
    }
    const subFields = field.subFields.map((sub) => {
      const subKey = (sub.fieldKey ?? '').toLowerCase();
      let options: SubmissionMandatoryFieldOption[] | undefined;
      if (PAY_BILL_CURRENCY_SUB_KEYS.has(subKey) && currency.length) {
        options = currency;
      } else if (PAY_BILL_TAX_TERM_SUB_KEYS.has(subKey) && taxTerm.length) {
        options = taxTerm;
      } else if (PAY_BILL_EMPLOYMENT_SUB_KEYS.has(subKey) && employment.length) {
        options = employment;
      } else {
        return sub;
      }
      changed = true;
      return {
        ...sub,
        fieldType: 'select' as const,
        options,
      };
    });
    return changed ? { ...field, subFields } : field;
  });
  return changed ? next : fields;
}

function isAdditionalNotifiersFieldKey(fieldKey: string | null | undefined): boolean {
  const key = (fieldKey ?? '').toLowerCase();
  return key === 'additional_notifiers' || key === 'additional_notifications';
}

/**
 * Attach ATS additional-notifier options onto `additional_notifiers`.
 * Uses `additional_notifiers_options` as the dropdown source and merges
 * selected options so preselected ids always have labels.
 */
export function applyAdditionalNotifiersOptionsToFields(
  fields: SubmissionMandatoryFieldDefinition[],
  allOptions: SubmissionMandatoryFieldOption[] | null | undefined,
  selectedOptions?: SubmissionMandatoryFieldOption[] | null,
): SubmissionMandatoryFieldDefinition[] {
  if (!fields.length) {
    return fields;
  }
  const all = allOptions ?? [];
  const selected = selectedOptions ?? [];
  if (!all.length && !selected.length) {
    return fields;
  }
  let changed = false;
  const next = fields.map((field) => {
    if (!isAdditionalNotifiersFieldKey(field.fieldKey)) {
      return field;
    }
    const base = all.length > 0 ? all : (field.options ?? []);
    const byValue = new Map(base.map((o) => [String(o.value), o]));
    for (const opt of selected) {
      if (!byValue.has(String(opt.value))) {
        byValue.set(String(opt.value), opt);
      }
    }
    changed = true;
    return {
      ...field,
      fieldType: 'multiselect' as const,
      options: [...byValue.values()],
    };
  });
  return changed ? next : fields;
}

/**
 * Normalize ATS job_posting_id → scalar maps (e.g. selected_currency_codes).
 * Values are stored as trimmed strings for select/number hydration.
 */
export function coerceSelectedScalarByJob(raw: unknown): SelectedScalarByJob {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: SelectedScalarByJob = {};
  for (const [jobId, rawVal] of Object.entries(value as Record<string, unknown>)) {
    const id = String(jobId ?? '').trim();
    if (!id) {
      continue;
    }
    if (rawVal == null) {
      continue;
    }
    if (typeof rawVal === 'string' || typeof rawVal === 'number' || typeof rawVal === 'boolean') {
      const s = String(rawVal).trim();
      if (s !== '') {
        out[id] = s;
      }
    }
  }
  return out;
}

/** Resolve one scalar default for a job posting (tolerant of string/number id forms). */
export function selectedScalarForJob(
  byJob: SelectedScalarByJob | null | undefined,
  jobPostingId: string | number | null | undefined,
): string {
  if (!byJob) {
    return '';
  }
  for (const key of entityIdLookupKeys(jobPostingId)) {
    if (Object.prototype.hasOwnProperty.call(byJob, key)) {
      const v = String(byJob[key] ?? '').trim();
      if (v) {
        return v;
      }
    }
  }
  const want = Number(String(jobPostingId ?? '').trim());
  if (Number.isFinite(want) && want > 0) {
    for (const [key, val] of Object.entries(byJob)) {
      const have = Number(key);
      if (Number.isFinite(have) && have === want) {
        const v = String(val ?? '').trim();
        if (v) {
          return v;
        }
      }
    }
  }
  return '';
}

/** Resolve one scalar default trying multiple entity ids (pipeline → job, etc.). */
export function selectedScalarForAnyId(
  byJob: SelectedScalarByJob | null | undefined,
  ...entityIds: Array<string | number | null | undefined>
): string {
  for (const id of entityIds) {
    const v = selectedScalarForJob(byJob, id);
    if (v) {
      return v;
    }
  }
  return '';
}

/** Coerce all pay/bill-rate selected_* maps from getMandatoryFields. */
export function coerceSelectedPayBillRateDefaults(raw: {
  selected_currency_codes?: unknown;
  selected_bill_rates?: unknown;
  selected_tax_terms?: unknown;
  selected_employmnet_types?: unknown;
  selected_billrate_currency_codes?: unknown;
  selected_submission_billrates?: unknown;
  selected_billrate_tax_terms?: unknown;
  selected_billrate_employmnet_types?: unknown;
} | null | undefined): SelectedPayBillRateDefaultsByJob {
  return {
    currencyCodes: coerceSelectedScalarByJob(raw?.selected_currency_codes),
    billRates: coerceSelectedScalarByJob(raw?.selected_bill_rates),
    taxTerms: coerceSelectedScalarByJob(raw?.selected_tax_terms),
    employmentTypes: coerceSelectedScalarByJob(raw?.selected_employmnet_types),
    billrateCurrencyCodes: coerceSelectedScalarByJob(raw?.selected_billrate_currency_codes),
    submissionBillrates: coerceSelectedScalarByJob(raw?.selected_submission_billrates),
    billrateTaxTerms: coerceSelectedScalarByJob(raw?.selected_billrate_tax_terms),
    billrateEmploymentTypes: coerceSelectedScalarByJob(raw?.selected_billrate_employmnet_types),
  };
}

export function emptySelectedPayBillRateDefaults(): SelectedPayBillRateDefaultsByJob {
  return {
    currencyCodes: {},
    billRates: {},
    taxTerms: {},
    employmentTypes: {},
    billrateCurrencyCodes: {},
    submissionBillrates: {},
    billrateTaxTerms: {},
    billrateEmploymentTypes: {},
  };
}

/**
 * Pipeline-convert scalar defaults (availability, relocate, video, emails, comments).
 * Keyed by pipeline_id and/or job_posting_id from getMandatoryFields.
 */
export type SelectedPipelineScalarDefaultsByJob = {
  dateOfAvailable: SelectedScalarByJob;
  videoLinks: SelectedScalarByJob;
  relocate: SelectedScalarByJob;
  addExternalsEmailids: SelectedScalarByJob;
  submissionComments: SelectedScalarByJob;
};

export function emptySelectedPipelineScalarDefaults(): SelectedPipelineScalarDefaultsByJob {
  return {
    dateOfAvailable: {},
    videoLinks: {},
    relocate: {},
    addExternalsEmailids: {},
    submissionComments: {},
  };
}

/** Coerce selected_* scalar maps used outside Pay/Bill Rate multifields. */
export function coerceSelectedPipelineScalarDefaults(raw: {
  selected_date_of_available?: unknown;
  selected_video_links?: unknown;
  selected_relocate?: unknown;
  selected_add_externals_emailids?: unknown;
  selected_submission_comments?: unknown;
} | null | undefined): SelectedPipelineScalarDefaultsByJob {
  return {
    dateOfAvailable: coerceSelectedScalarByJob(raw?.selected_date_of_available),
    videoLinks: coerceSelectedScalarByJob(raw?.selected_video_links),
    relocate: coerceSelectedScalarByJob(raw?.selected_relocate),
    addExternalsEmailids: coerceSelectedScalarByJob(raw?.selected_add_externals_emailids),
    submissionComments: coerceSelectedScalarByJob(raw?.selected_submission_comments),
  };
}

/**
 * Resolve a prefilled scalar for a wizard field key from pipeline convert maps.
 * Tries pipeline_id first, then job_posting_id.
 */
export function preselectedPipelineScalarForField(
  defaults: SelectedPipelineScalarDefaultsByJob | null | undefined,
  fieldKey: string,
  jobPostingId: string,
  pipelineId?: string | null,
): string {
  if (!defaults) {
    return '';
  }
  const key = (fieldKey ?? '').trim().toLowerCase();
  let map: SelectedScalarByJob | undefined;
  switch (key) {
    case 'date_of_available':
      map = defaults.dateOfAvailable;
      break;
    case 'video_link':
      map = defaults.videoLinks;
      break;
    case 'relocate':
      map = defaults.relocate;
      break;
    case 'add_externals_emailids':
      map = defaults.addExternalsEmailids;
      break;
    case 'submission_comments':
      map = defaults.submissionComments;
      break;
    default:
      return '';
  }
  return selectedScalarForAnyId(map, pipelineId, jobPostingId);
}

/**
 * Preselected Pay Rate group for `bill_rate` multifield
 * (currency_code, bill_rate, tax_term, employmnet_type).
 * Tries pipeline_id first (convert flow), then job_posting_id.
 */
export function preselectedPayRateGroupForJob(
  defaults: SelectedPayBillRateDefaultsByJob | null | undefined,
  jobPostingId: string,
  pipelineId?: string | null,
): Record<string, string> | null {
  if (!defaults) {
    return null;
  }
  const currency_code = selectedScalarForAnyId(
    defaults.currencyCodes,
    pipelineId,
    jobPostingId,
  );
  const bill_rate = selectedScalarForAnyId(defaults.billRates, pipelineId, jobPostingId);
  const tax_term = selectedScalarForAnyId(defaults.taxTerms, pipelineId, jobPostingId);
  const employmnet_type = selectedScalarForAnyId(
    defaults.employmentTypes,
    pipelineId,
    jobPostingId,
  );
  if (!currency_code && !bill_rate && !tax_term && !employmnet_type) {
    return null;
  }
  return {
    currency_code,
    bill_rate,
    tax_term,
    employmnet_type,
  };
}

/**
 * Preselected Bill Rate group for `submission_billrate` multifield
 * (billrate_currency_code, submission_billrate, billrate_tax_term, billrate_employmnet_type).
 * Tries pipeline_id first (convert flow), then job_posting_id.
 */
export function preselectedSubmissionBillrateGroupForJob(
  defaults: SelectedPayBillRateDefaultsByJob | null | undefined,
  jobPostingId: string,
  pipelineId?: string | null,
): Record<string, string> | null {
  if (!defaults) {
    return null;
  }
  const billrate_currency_code = selectedScalarForAnyId(
    defaults.billrateCurrencyCodes,
    pipelineId,
    jobPostingId,
  );
  const submission_billrate = selectedScalarForAnyId(
    defaults.submissionBillrates,
    pipelineId,
    jobPostingId,
  );
  const billrate_tax_term = selectedScalarForAnyId(
    defaults.billrateTaxTerms,
    pipelineId,
    jobPostingId,
  );
  const billrate_employmnet_type = selectedScalarForAnyId(
    defaults.billrateEmploymentTypes,
    pipelineId,
    jobPostingId,
  );
  if (
    !billrate_currency_code &&
    !submission_billrate &&
    !billrate_tax_term &&
    !billrate_employmnet_type
  ) {
    return null;
  }
  return {
    billrate_currency_code,
    submission_billrate,
    billrate_tax_term,
    billrate_employmnet_type,
  };
}

/** Allowed resume upload extensions for `select_resume` = `new`. */
export const SUBMISSION_RESUME_UPLOAD_EXTENSIONS = ['pdf', 'doc', 'txt'] as const;

/**
 * Known MIME types for resume upload. Extension remains authoritative —
 * browsers (especially Windows) often report nonstandard MIME for `.doc`.
 */
export const SUBMISSION_RESUME_UPLOAD_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/x-msword',
  'application/doc',
  'application/ms-doc',
  'text/plain',
  'text/txt',
] as const;

/** Max resume upload size (5 MB). */
export const SUBMISSION_RESUME_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

export type SubmissionResumeUploadPayload = {
  file_name: string;
  mime_type: string;
  content_base64: string;
};

/** job_seeker_id → resume options for the `select_resume` dropdown. */
export type ResumeOptionsBySeeker = Record<string, SubmissionMandatoryFieldOption[]>;

/**
 * Normalize ATS `resume_options` / `selected_resume_options` into seeker-keyed option lists.
 * Shape: `{ "<job_seeker_id>": { "new": "Upload new Resume", "<doc_id>": "<label>", ... }, ... }`.
 */
export function coerceResumeOptions(raw: unknown): ResumeOptionsBySeeker {
  return coerceIdToOptionsMap(raw);
}

/** Resolve resume options for one applicant. */
export function resumeOptionsForJobSeeker(
  bySeeker: ResumeOptionsBySeeker | null | undefined,
  jobSeekerId: string,
): SubmissionMandatoryFieldOption[] {
  return optionsForEntityId(bySeeker, jobSeekerId);
}

/** Preselected resume values (ids) for one applicant. */
export function selectedResumeValuesForJobSeeker(
  bySeeker: ResumeOptionsBySeeker | null | undefined,
  jobSeekerId: string,
): string[] {
  return resumeOptionsForJobSeeker(bySeeker, jobSeekerId).map((o) => String(o.value));
}

/**
 * Preselected resume ids — tries pipeline_id, job_seeker_id, then job_posting_id
 * (ATS may key selected_resume_options by any of these).
 */
export function selectedResumeValuesForPair(
  byId: ResumeOptionsBySeeker | null | undefined,
  jobSeekerId: string | null | undefined,
  jobPostingId?: string | null,
  pipelineId?: string | null,
): string[] {
  for (const id of [pipelineId, jobSeekerId, jobPostingId]) {
    const opts = optionsForEntityId(byId, id);
    if (opts.length) {
      return opts.map((o) => String(o.value));
    }
  }
  return [];
}

/**
 * Attach per-applicant resume options onto `select_resume` (same pattern as eform_ids).
 * Always rewrites the field so the dropdown receives a concrete options array.
 * Merges selected options from seeker / job / pipeline keys when present.
 */
export function applyResumeOptionsToFields(
  fields: SubmissionMandatoryFieldDefinition[],
  jobSeekerId: string,
  allBySeeker: ResumeOptionsBySeeker | null | undefined,
  selectedBySeeker?: ResumeOptionsBySeeker | null,
  jobPostingId?: string | null,
  pipelineId?: string | null,
): SubmissionMandatoryFieldDefinition[] {
  if (!fields.length) {
    return fields;
  }
  const seekerId = String(jobSeekerId ?? '').trim();
  const all = resumeOptionsForJobSeeker(allBySeeker, seekerId);
  const selectedByValue = new Map<string, SubmissionMandatoryFieldOption>();
  for (const id of [pipelineId, seekerId, jobPostingId]) {
    for (const opt of optionsForEntityId(selectedBySeeker, id)) {
      selectedByValue.set(String(opt.value), opt);
    }
  }
  const selected = [...selectedByValue.values()];
  let changed = false;
  const next = fields.map((field) => {
    if ((field.fieldKey ?? '').toLowerCase() !== 'select_resume') {
      return field;
    }
    const byValue = new Map<string, SubmissionMandatoryFieldOption>();
    for (const opt of all) {
      byValue.set(String(opt.value), opt);
    }
    for (const opt of selected) {
      if (!byValue.has(String(opt.value))) {
        byValue.set(String(opt.value), opt);
      }
    }
    // Keep "Upload new Resume" first when present.
    const options = [...byValue.values()].sort((a, b) => {
      if (String(a.value) === 'new') {
        return -1;
      }
      if (String(b.value) === 'new') {
        return 1;
      }
      return 0;
    });
    changed = true;
    return {
      ...field,
      fieldType: 'select' as const,
      options,
    };
  });
  return changed ? next : fields;
}

/**
 * Resolve dropdown options for a mandatory field at render time.
 * Ensures seeker-keyed maps (`resume_options`, `verified_eform_options`) always win
 * over empty catalog options on `select_resume` / `eform_ids`.
 */
export function resolveMandatoryFieldOptions(
  field: SubmissionMandatoryFieldDefinition,
  jobSeekerId: string,
  opts: {
    resumeOptionsBySeeker?: ResumeOptionsBySeeker | null;
    selectedResumeOptionsBySeeker?: ResumeOptionsBySeeker | null;
    verifiedEformOptionsBySeeker?: VerifiedEformOptionsBySeeker | null;
  },
): SubmissionMandatoryFieldOption[] {
  const key = (field.fieldKey ?? '').toLowerCase();
  const seekerId = String(jobSeekerId ?? '').trim();
  if (key === 'select_resume') {
    // Prefer options already merged onto the field (seeker + job/pipeline selected).
    if (field.options?.length) {
      return field.options;
    }
    const all = resumeOptionsForJobSeeker(opts.resumeOptionsBySeeker, seekerId);
    const selected = resumeOptionsForJobSeeker(opts.selectedResumeOptionsBySeeker, seekerId);
    if (all.length || selected.length) {
      const byValue = new Map<string, SubmissionMandatoryFieldOption>();
      for (const opt of all) {
        byValue.set(String(opt.value), opt);
      }
      for (const opt of selected) {
        if (!byValue.has(String(opt.value))) {
          byValue.set(String(opt.value), opt);
        }
      }
      return [...byValue.values()].sort((a, b) => {
        if (String(a.value) === 'new') {
          return -1;
        }
        if (String(b.value) === 'new') {
          return 1;
        }
        return 0;
      });
    }
  }
  if (key === 'eform_ids') {
    const eformOptions = verifiedEformOptionsForJobSeeker(
      opts.verifiedEformOptionsBySeeker,
      seekerId,
    );
    if (eformOptions.length) {
      return eformOptions;
    }
  }
  return field.options ?? [];
}

function resumeExtensionFromFileName(fileName: string): string {
  const name = (fileName ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) {
    return '';
  }
  return name.slice(dot + 1).toLowerCase();
}

/** True when extension is one of pdf/doc/txt (case-insensitive). */
export function isAllowedSubmissionResumeExtension(fileName: string): boolean {
  const ext = resumeExtensionFromFileName(fileName);
  return (SUBMISSION_RESUME_UPLOAD_EXTENSIONS as readonly string[]).includes(ext);
}

/**
 * True when MIME is an allowed resume type, empty, or generic octet-stream.
 * Prefer {@link isAllowedSubmissionResumeExtension} — MIME alone is unreliable.
 */
export function isAllowedSubmissionResumeMimeType(mimeType: string | null | undefined): boolean {
  const mime = (mimeType ?? '').trim().toLowerCase();
  if (!mime || mime === 'application/octet-stream') {
    return true;
  }
  return (SUBMISSION_RESUME_UPLOAD_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * Validate a resume file for `select_resume` = `new`.
 * Extension is case-insensitive and authoritative; MIME is only a fallback when
 * the file has no extension (browsers often send odd MIME for `.doc`).
 */
export function validateSubmissionResumeUploadFile(file: {
  name: string;
  type?: string | null;
  size?: number | null;
}): string | null {
  const name = (file.name ?? '').trim();
  if (!name) {
    return 'Please choose a resume file.';
  }
  if (!isAllowedSubmissionResumeExtension(name)) {
    const ext = resumeExtensionFromFileName(name);
    // Unknown extension → reject. Missing extension → allow known MIME only.
    if (ext || !isAllowedSubmissionResumeMimeType(file.type)) {
      return 'Resume must be a PDF, DOC, or TXT file.';
    }
  }
  const size = typeof file.size === 'number' ? file.size : 0;
  if (size <= 0) {
    return 'Resume file is empty.';
  }
  if (size > SUBMISSION_RESUME_UPLOAD_MAX_BYTES) {
    return 'Resume file must be 5 MB or smaller.';
  }
  return null;
}

/** Unique comma-separated business unit ids from selected pairs (for getMandatoryFields). */
export function uniqueBusinessUnitIdsFromPairs(pairs: SubmissionChoicePair[]): string {
  const ids = new Set<string>();
  for (const pair of pairs) {
    const bu = (pair.businessUnitId ?? '').trim();
    if (bu) {
      ids.add(bu);
    }
  }
  return [...ids].join(',');
}

/** Normalize ATS tenant host to a base URL without trailing slash. */
export function normalizeAtsThDomain(raw: string | null | undefined): string {
  let s = (raw ?? '').trim();
  if (!s) {
    return '';
  }
  if (!/^https?:\/\//i.test(s)) {
    s = `http://${s.replace(/^\/+/, '')}`;
  }
  return s.replace(/\/+$/, '');
}

export function buildAtsAgentsUrl(
  thDomain: string | null | undefined,
  action: string,
): string {
  const base = normalizeAtsThDomain(thDomain);
  const path = (action ?? '').trim().replace(/^\/+/, '');
  if (!base || !path) {
    return '';
  }
  return `${base}/AiAgents/${path}`;
}

function readPrimarySkillFromChoiceRow(row: Record<string, unknown>): string | null {
  return (
    asTrimmedString(
      row['primary_skill'] ??
        row['primarySkill'] ??
        row['primary skill'] ??
        row['primary_job_title'] ??
        row['primaryJobTitle'] ??
        row['primary job title'],
    ) || null
  );
}

/** Positive int / non-empty string id from API lookup rows. */
function asIdString(v: unknown): string {
  if (typeof v === 'string' && v.trim()) {
    return v.trim();
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return String(Math.floor(v));
  }
  return '';
}

function pairKey(jobPostingId: string, jobSeekerId: string, submissionId?: string | null): string {
  const sid = (submissionId ?? '').trim();
  if (sid) {
    return `sub:${sid}`;
  }
  return `${jobPostingId.trim()}|${jobSeekerId.trim()}`;
}

export { pairKey };

/** Parse `lookup_rows` from paused `submission_agent` task output. */
export function parseSubmissionChoicePairsFromTask(
  task: TaskGraphTaskDto,
): SubmissionChoicePair[] {
  const out = asRecord(task.output);
  const raw = out?.['lookup_rows'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const pairs: SubmissionChoicePair[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row) {
      continue;
    }
    const jobPostingId = asTrimmedString(row['job_posting_id']);
    const jobSeekerId = asTrimmedString(row['job_seeker_id']);
    if (!jobPostingId || !jobSeekerId) {
      continue;
    }
    const key = pairKey(jobPostingId, jobSeekerId);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    pairs.push({
      jobPostingId,
      jobSeekerId,
      applicantName: asTrimmedString(row['applicant_name']),
      jobCode: asTrimmedString(row['job_code']),
      jobTitle: asTrimmedString(row['job_title']),
      matchScore: parseMatchScore(
        row['match_score'] ?? row['matching_score'] ?? row['score'],
      ),
      primarySkill: readPrimarySkillFromChoiceRow(row),
      ...(asTrimmedString(row['email'] ?? row['applicant_email'])
        ? { applicantEmail: asTrimmedString(row['email'] ?? row['applicant_email']) }
        : {}),
      ...(asTrimmedString(row['mobile'] ?? row['phone'] ?? row['mobile_number'])
        ? {
            applicantMobile: asTrimmedString(
              row['mobile'] ?? row['phone'] ?? row['mobile_number'],
            ),
          }
        : {}),
      ...(asTrimmedString(row['location'] ?? row['applicant_location'])
        ? {
            applicantLocation: asTrimmedString(
              row['location'] ?? row['applicant_location'],
            ),
          }
        : (() => {
            const composed = [
              asTrimmedString(row['city']),
              asTrimmedString(row['state']),
              asTrimmedString(row['country']),
            ]
              .filter((p) => p.length > 0)
              .join(', ');
            return composed ? { applicantLocation: composed } : {};
          })()),
      ...(row['already_submitted'] === true ||
      row['already_submitted'] === 1 ||
      row['already_submitted'] === '1' ||
      row['already_submitted'] === 'true'
        ? { alreadySubmitted: true as const }
        : {}),
      // Existing-pair annotate puts display code in `submission_id`, PK in `applied_job_id`.
      ...(asIdString(row['submission_display_id']) ||
      asIdString(row['submissionDisplayId']) ||
      asIdString(row['submission_id']) ||
      asIdString(row['submissionId'])
        ? {
            submissionDisplayId:
              asIdString(row['submission_display_id']) ||
              asIdString(row['submissionDisplayId']) ||
              asIdString(row['submission_id']) ||
              asIdString(row['submissionId']),
          }
        : {}),
      ...(asIdString(row['applied_job_id']) || asIdString(row['appliedJobId'])
        ? {
            submissionId:
              asIdString(row['applied_job_id']) || asIdString(row['appliedJobId']),
          }
        : {}),
      ...(asIdString(row['pipeline_id']) || asIdString(row['pipelineId'])
        ? {
            pipelineId:
              asIdString(row['pipeline_id']) || asIdString(row['pipelineId']),
          }
        : {}),
      ...(asTrimmedString(
        row['job_encrypted_id'] ?? row['jobEncryptedID'] ?? row['jobEncryptedId'],
      )
        ? {
            jobEncryptedId: asTrimmedString(
              row['job_encrypted_id'] ?? row['jobEncryptedID'] ?? row['jobEncryptedId'],
            ),
          }
        : {}),
      ...(asTrimmedString(
        row['applicant_encrypted_id'] ??
          row['applicantEncryptedID'] ??
          row['applicantEncryptedId'],
      )
        ? {
            applicantEncryptedId: asTrimmedString(
              row['applicant_encrypted_id'] ??
                row['applicantEncryptedID'] ??
                row['applicantEncryptedId'],
            ),
          }
        : {}),
      ...(asTrimmedString(
        row['submission_encrypted_id'] ??
          row['submissionEncryptedID'] ??
          row['submissionEncryptedId'],
      )
        ? {
            submissionEncryptedId: asTrimmedString(
              row['submission_encrypted_id'] ??
                row['submissionEncryptedID'] ??
                row['submissionEncryptedId'],
            ),
          }
        : {}),
      ...(readBusinessUnitIdFromChoiceRow(row)
        ? { businessUnitId: readBusinessUnitIdFromChoiceRow(row)! }
        : {}),
    });
  }
  return pairs;
}

/** Parse interview-schedule / Voice AI choice rows (`submission_id` + display fields). */
export function parseInterviewScheduleChoicePairsFromTask(
  task: TaskGraphTaskDto,
): SubmissionChoicePair[] {
  const out = asRecord(task.output);
  const raw = out?.['lookup_rows'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const pairs: SubmissionChoicePair[] = [];
  for (const item of raw) {
    if (pairs.length >= INTERVIEW_SCHEDULE_MAX_CHOICE_ROWS) {
      break;
    }
    const row = asRecord(item);
    if (!row) {
      continue;
    }
    // `submission_id` is the row PK (`job_seeker_applied_jobs.id`) for API selections.
    const submissionId =
      asIdString(row['submission_id']) || asIdString(row['submissionId']);
    if (!submissionId) {
      continue;
    }
    // Recruiter-facing display code — never fall back to the PK for UI labels.
    const submissionDisplayId =
      asIdString(row['submission_display_id']) ||
      asIdString(row['submissionDisplayId']) ||
      asIdString(row['submission_code']) ||
      asIdString(row['submissionCode']) ||
      null;
    const key = pairKey('', '', submissionId);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    pairs.push({
      jobPostingId: asTrimmedString(row['job_posting_id'] ?? row['jobs_id']) || `job-${submissionId}`,
      jobSeekerId:
        asTrimmedString(row['job_seeker_id'] ?? row['applicant_id']) || `seeker-${submissionId}`,
      applicantName: asTrimmedString(
        row['applicant_name'] ?? row['candidate_name'] ?? row['name'],
      ),
      jobCode: asTrimmedString(row['job_code']),
      jobTitle: asTrimmedString(row['job_title'] ?? row['position_title']),
      matchScore: null,
      submissionId,
      ...(submissionDisplayId ? { submissionDisplayId } : {}),
      submissionStatus: asTrimmedString(row['submission_status'] ?? row['status']) || null,
      applicantEmail: asTrimmedString(row['applicant_email'] ?? row['email']) || null,
      applicantMobile:
        asTrimmedString(row['mobile'] ?? row['phone'] ?? row['mobile_number']) || null,
      applicantLocation:
        asTrimmedString(row['location'] ?? row['applicant_location']) ||
        [asTrimmedString(row['city']), asTrimmedString(row['state']), asTrimmedString(row['country'])]
          .filter((p) => p.length > 0)
          .join(', ') ||
        null,
      primarySkill: readPrimarySkillFromChoiceRow(row),
      recruiterName: asTrimmedString(row['recruiter_name']) || null,
      ...(asTrimmedString(
        row['applicant_encrypted_id'] ??
          row['applicantEncryptedID'] ??
          row['applicantEncryptedId'],
      )
        ? {
            applicantEncryptedId: asTrimmedString(
              row['applicant_encrypted_id'] ??
                row['applicantEncryptedID'] ??
                row['applicantEncryptedId'],
            ),
          }
        : {}),
      ...(asTrimmedString(
        row['job_encrypted_id'] ?? row['jobEncryptedID'] ?? row['jobEncryptedId'],
      )
        ? {
            jobEncryptedId: asTrimmedString(
              row['job_encrypted_id'] ?? row['jobEncryptedID'] ?? row['jobEncryptedId'],
            ),
          }
        : {}),
      ...(asTrimmedString(
        row['submission_encrypted_id'] ??
          row['submissionEncryptedID'] ??
          row['submissionEncryptedId'],
      )
        ? {
            submissionEncryptedId: asTrimmedString(
              row['submission_encrypted_id'] ??
                row['submissionEncryptedID'] ??
                row['submissionEncryptedId'],
            ),
          }
        : {}),
    });
  }
  return pairs;
}

/** Alias — Voice AI choice rows use the same lookup_rows shape. */
export function parseVoiceAiChoicePairsFromTask(
  task: TaskGraphTaskDto,
): SubmissionChoicePair[] {
  return parseInterviewScheduleChoicePairsFromTask(task);
}

function parseMatchScore(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.round(value);
    return n >= 0 && n <= 100 ? n : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number.parseFloat(value.replace(/%/g, '').trim());
    if (!Number.isFinite(n)) {
      return null;
    }
    const rounded = Math.round(n);
    return rounded >= 0 && rounded <= 100 ? rounded : null;
  }
  return null;
}

/** Display helpers — missing fields render as N/A. */
export function displayOrNa(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  return t.length > 0 ? t : 'N/A';
}

export function formatSubmissionChoiceJobLine(pair: SubmissionChoicePair): string {
  const code = displayOrNa(pair.jobCode);
  const title = displayOrNa(pair.jobTitle);
  return `${code} · ${title}`;
}

/** Unique job labels from pairs (`code · title` when title exists), comma-separated. */
export function formatUniqueSubmissionJobCodes(pairs: SubmissionChoicePair[]): string {
  return summarizeUniqueChoiceJobs(pairs)
    .map((j) => j.label)
    .join(', ');
}

export interface SubmissionExistingJobSummary {
  jobCode: string;
  jobTitle: string;
  label: string;
  count: number;
  jobEncryptedId: string | null;
}

/**
 * Unique jobs from choice pairs (for subtitle links).
 * Prefer encrypted id from any pair that has one for the same job code/title.
 */
export function summarizeUniqueChoiceJobs(
  pairs: SubmissionChoicePair[],
): SubmissionExistingJobSummary[] {
  const byJob = new Map<string, SubmissionExistingJobSummary>();
  for (const pair of pairs) {
    const code = (pair.jobCode ?? '').trim() || (pair.jobPostingId ?? '').trim();
    if (!code) {
      continue;
    }
    const title = (pair.jobTitle ?? '').trim();
    const key = `${code}|${title}`;
    const prev = byJob.get(key);
    if (prev) {
      prev.count += 1;
      if (!prev.jobEncryptedId && pair.jobEncryptedId) {
        prev.jobEncryptedId = pair.jobEncryptedId.trim() || null;
      }
      continue;
    }
    byJob.set(key, {
      jobCode: code,
      jobTitle: title,
      label: title ? `${code} · ${title}` : code,
      count: 1,
      jobEncryptedId: (pair.jobEncryptedId ?? '').trim() || null,
    });
  }
  return [...byJob.values()];
}

/** Distinct job posting ids from selected pairs (comma-separated). */
export function uniqueJobPostingIdsFromPairs(pairs: SubmissionChoicePair[]): string {
  const ids = new Set<string>();
  for (const pair of pairs) {
    const id = (pair.jobPostingId ?? '').trim();
    if (id) {
      ids.add(id);
    }
  }
  return [...ids].join(',');
}

/** Distinct job seeker ids from selected pairs (comma-separated). */
export function uniqueJobSeekerIdsFromPairs(pairs: SubmissionChoicePair[]): string {
  const ids = new Set<string>();
  for (const pair of pairs) {
    const id = (pair.jobSeekerId ?? '').trim();
    if (id) {
      ids.add(id);
    }
  }
  return [...ids].join(',');
}

/** Distinct pipeline row PKs from selected pairs converting pipeline → submission. */
export function uniquePipelineIdsFromPairs(pairs: SubmissionChoicePair[]): string {
  const ids = new Set<string>();
  for (const pair of pairs) {
    const id = (pair.pipelineId ?? '').trim();
    if (id) {
      ids.add(id);
    }
  }
  return [...ids].join(',');
}

/** Loose tenant parse for mandatory-field fetch (does not require recruiter email). */
export function parseChoiceTenantContext(raw: unknown): {
  th_domain: string;
  th_company_id: number;
  th_db_type: number;
} | null {
  const o =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  if (!o) {
    return null;
  }
  const thDomain = asTrimmedString(o['th_domain'] ?? o['thDomain']);
  const thCompanyId = Number(o['th_company_id'] ?? o['thCompanyId']);
  const thDbType = Number(o['th_db_type'] ?? o['thDbType']);
  if (!thDomain || !Number.isFinite(thCompanyId) || thCompanyId <= 0) {
    return null;
  }
  return {
    th_domain: thDomain,
    th_company_id: Math.floor(thCompanyId),
    th_db_type: Number.isFinite(thDbType) && thDbType >= 0 ? Math.floor(thDbType) : 1,
  };
}

export interface GetMandatoryFieldsApiRequest {
  action_context?: ChoiceActionContext;
  business_unit_id?: string;
  job_posting_ids?: string;
  job_seeker_ids?: string;
  /** Comma-separated pipeline row PKs when converting pipeline → submission. */
  pipeline_ids?: string;
  module_id?: number;
}

/** Aggregate already-submitted pairs by job for the choice-panel subtitle. */
export function summarizeExistingSubmissionJobs(
  pairs: SubmissionChoicePair[],
): SubmissionExistingJobSummary[] {
  return summarizeUniqueChoiceJobs(pairs.filter((p) => p.alreadySubmitted));
}

export function countAlreadySubmittedPairs(pairs: SubmissionChoicePair[]): number {
  return pairs.filter((p) => p.alreadySubmitted).length;
}

export function actionableSubmissionChoicePairs(
  pairs: SubmissionChoicePair[],
): SubmissionChoicePair[] {
  return pairs.filter((p) => !p.alreadySubmitted);
}

export function formatSubmissionChoicePairLabel(pair: SubmissionChoicePair): string {
  return `${displayOrNa(pair.applicantName)} — ${formatSubmissionChoiceJobLine(pair)}`;
}

export function initialsFromName(name: string | null | undefined): string {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) {
    return 'NA';
  }
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

const AVATAR_PALETTES = [
  { bg: '#dbeafe', fg: '#1e40af' },
  { bg: '#d1fae5', fg: '#065f46' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#fce7f3', fg: '#9d174d' },
  { bg: '#e0e7ff', fg: '#3730a3' },
] as const;

export function avatarPaletteForKey(key: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[idx]!;
}

export function taskIsSubmissionChoiceWait(task: TaskGraphTaskDto | null | undefined): boolean {
  const waitingFor = asTrimmedString(task?.waitingFor).toLowerCase();
  if (waitingFor === SUBMISSION_CHOICE_WAITING_FOR) {
    return true;
  }
  const out = asRecord(task?.output);
  return (
    out?.['type'] === 'submission_choice_needed' ||
    out?.['type'] === 'pipeline_choice_needed'
  );
}

export function taskIsInterviewScheduleChoiceWait(
  task: TaskGraphTaskDto | null | undefined,
): boolean {
  const waitingFor = asTrimmedString(task?.waitingFor).toLowerCase();
  if (waitingFor === INTERVIEW_SCHEDULE_CHOICE_WAITING_FOR) {
    return true;
  }
  const out = asRecord(task?.output);
  if (out?.['type'] === INTERVIEW_SCHEDULE_CHOICE_OUTPUT_TYPE) {
    return true;
  }
  const tool = asTrimmedString(task?.toolName).toLowerCase();
  if (
    tool !== 'interview_scheduling_agent' &&
    tool !== 'interview_scheduling' &&
    tool !== 'schedule_interview'
  ) {
    return false;
  }
  if (!Array.isArray(out?.['lookup_rows'])) {
    return false;
  }
  return (out!['lookup_rows'] as unknown[]).some((item) => {
    const row = asRecord(item);
    if (!row) {
      return false;
    }
    return row['submission_id'] != null || row['submissionId'] != null;
  });
}

export function taskIsVoiceAiChoiceWait(
  task: TaskGraphTaskDto | null | undefined,
): boolean {
  const waitingFor = asTrimmedString(task?.waitingFor).toLowerCase();
  if (waitingFor === VOICE_AI_CHOICE_WAITING_FOR) {
    return true;
  }
  const out = asRecord(task?.output);
  if (out?.['type'] === VOICE_AI_CHOICE_OUTPUT_TYPE) {
    return true;
  }
  return false;
}

export function taskGraphRowIsInterviewSchedulingAgent(
  task: TaskGraphTaskDto | null | undefined,
): boolean {
  const tool = asTrimmedString(task?.toolName).toLowerCase();
  return (
    tool === 'interview_scheduling_agent' ||
    tool === 'interview_scheduling' ||
    tool === 'schedule_interview'
  );
}
